// src/components/Devices/DevicesTable.jsx
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUnit } from 'effector-react';
import {
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Chip,
  Tooltip,
  Pagination,
  Stack,
  IconButton,
  CircularProgress,
  Collapse,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  TableSortLabel,
  TextField,
  MenuItem,
  Button,
  LinearProgress,
} from '@mui/material';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import PowerRoundedIcon from '@mui/icons-material/PowerRounded';

import EditDeviceDialog from './EditDeviceDialog';
import ReplaceDeviceDialog from './ReplaceDeviceDialog';
import ConfirmDialog from '../ConfirmModal';
import { $canManageDevices } from '../../store/auth';
import {
  $detachDeviceError,
  $detachingDeviceIds,
  detachDeviceClicked,
  detachDeviceErrorCleared,
} from '../../store/devices';
import { deviceReplacementsApi } from '../../api/deviceReplacements';

const ELECTRIC_TARIFF_ORDER = [
  { key: 'tariff1', label: 'День' },
  { key: 'tariff2', label: 'Ночь' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Все' },
  { value: 'ok', label: 'OK' },
  { value: 'warn', label: 'Внимание' },
  { value: 'error', label: 'Ошибка' },
  { value: 'NEW', label: 'Новый' },
  { value: 'REPLACED', label: 'Замена' },
];

const SYSTEM_TYPE_OPTIONS = [
  { value: '', label: 'Все' },
  { value: 'ХВС', label: 'ХВС' },
  { value: 'ГВС', label: 'ГВС' },
  { value: 'Отопление', label: 'Отопление' },
  { value: 'Электросчетчик', label: 'Электросчетчик' },
  { value: 'УРТ', label: 'УРТ' },
];

const EMPTY_FILTERS = {
  status: '',
  system_type: '',
  manufacturer: '',
  serial_number: '',
  room_number: '',
  suffix: '',
};

const FILTER_LABELS = {
  status: 'Статус',
  system_type: 'Тип счётчика',
  manufacturer: 'Производитель',
  serial_number: 'Серийный №',
  room_number: '№ помещения',
  suffix: 'Суффикс',
};

const numberFormatter = new Intl.NumberFormat('ru-RU', {
  maximumFractionDigits: 3,
});

const formatDateTime = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString('ru-RU');
};

const getDeviceArrivalTime = (device) => {
  return formatDateTime(device?.last_seen_at)
    || formatDateTime(device?.last_data_time)
    || formatDateTime(device?.latest_reading_ts);
};

const convertWhToKwh = (value) => {
  if (value === null || value === undefined || value === '') return value;
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return value;
  return num / 1000;
};

const formatReadingValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isFinite(num)) return numberFormatter.format(num);
  return String(value);
};

const isElectricityType = (value) => {
  if (!value) return false;
  const normalized = String(value)
    .toLowerCase()
    .replace('\u0451', '\u0435')
    .replace(/[\s_-]+/g, '');
  return (
    normalized.includes('\u044d\u043b\u0435\u043a\u0442\u0440\u043e') ||
    normalized.includes('electricity') ||
    normalized.includes('power')
  );
};

function StatusCell({ status }) {
  if (status === 'ok') {
    return <Chip size="small" color="success" variant="outlined" icon={<DoneRoundedIcon />} label="OK" />;
  }
  if (status === 'warn') {
    return <Chip size="small" color="warning" variant="outlined" icon={<ReportProblemRoundedIcon />} label="Внимание" />;
  }
  if (status === 'REPLACED') {
    return <Chip size="small" variant="outlined" label="Замена" />;
  }
  if (status === 'NEW') {
    return <Chip size="small" variant="outlined" label="Новый" />;
  }
  return <Chip size="small" color="error" variant="outlined" icon={<ReportProblemRoundedIcon />} label="Ошибка" />;
}

function DeviceReadingCell({ device }) {
  const isElectricity = device?.system_type === 'Электросчетчик';
  const components = device?.latest_components;

  if (isElectricity && components && typeof components === 'object') {
    const entries = ELECTRIC_TARIFF_ORDER.map(({ key, label }) => ({
      key,
      label,
      value: convertWhToKwh(components[key]),
    })).filter(({ value }) => value !== null && value !== undefined);

    if (entries.length) {
      return (
        <Stack spacing={0.5}>
          {entries.map(({ key, label, value }) => {
            return (
              <Chip
                key={key}
                size="small"
                variant="outlined"
                label={`${label}: ${formatReadingValue(value)}`}
                sx={{ justifyContent: 'flex-start' }}
              />
            );
          })}
        </Stack>
      );
    }
  }

  const baseValue =
    isElectricity && device?.last_data != null
      ? convertWhToKwh(device.last_data)
      : device?.last_data ?? device?.latest_reading_value;

  return <span>{formatReadingValue(baseValue)}</span>;
}

function DeviceHistoryReadingCell({ device }) {
  const isElectricity = isElectricityType(device?.system_type);
  const components = device?.history_components;

  if (isElectricity && components && typeof components === 'object') {
    const entries = ELECTRIC_TARIFF_ORDER.map(({ key, label }) => ({
      key,
      label,
      value: convertWhToKwh(components[key]),
    })).filter(({ value }) => value !== null && value !== undefined);

    if (entries.length) {
      return (
        <Stack spacing={0.5}>
          {entries.map(({ key, label, value }) => (
            <Chip
              key={key}
              size="small"
              variant="outlined"
              label={`${label}: ${formatReadingValue(value)}`}
              sx={{ justifyContent: 'flex-start' }}
            />
          ))}
        </Stack>
      );
    }
  }

  const baseValue =
    isElectricity && device?.history_value != null
      ? convertWhToKwh(device.history_value)
      : device?.history_value;

  return <span>{formatReadingValue(baseValue)}</span>;
}

function CellText({ value, title, maxWidth = 160 }) {
  const display = value ?? '—';
  return (
    <Tooltip title={title ?? display}>
      <span
        style={{
          display: 'inline-block',
          maxWidth,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          verticalAlign: 'bottom',
        }}
      >
        {display}
      </span>
    </Tooltip>
  );
}

function DevicesFiltersPanel({
  filters = {},
  onFiltersChange,
  roomNumberLabel = '№ помещения',
  houseMode = false,
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);

  const updateFilter = (key, value) => {
    if (!onFiltersChange) return;
    onFiltersChange({ ...filters, [key]: value });
  };

  const activeFilters = useMemo(() => {
    return Object.entries(filters || {})
      .filter(([key]) => !houseMode || (key !== 'room_number' && key !== 'suffix'))
      .filter(([, value]) => value != null && String(value).trim() !== '')
      .map(([key, value]) => ({
        key,
        label: key === 'room_number' ? roomNumberLabel : (FILTER_LABELS[key] || key),
        value: String(value),
      }));
  }, [filters, roomNumberLabel, houseMode]);

  const hasFilters = activeFilters.length > 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mb: 1.5,
        borderRadius: 2,
        background: 'rgba(0,0,0,0.02)',
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          role="button"
          tabIndex={0}
          onClick={() => setFiltersOpen((prev) => !prev)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setFiltersOpen((prev) => !prev);
            }
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 0.5,
            py: 0.375,
            mr: 0.5,
            borderRadius: 1.5,
            cursor: 'pointer',
            transition: 'background-color 160ms ease, transform 160ms ease',
            '&:hover': {
              backgroundColor: 'rgba(15, 23, 42, 0.04)',
            },
            '&:focus-visible': {
              outline: '2px solid rgba(25, 118, 210, 0.28)',
              outlineOffset: 1,
            },
            '&:active': {
              transform: 'translateY(1px)',
            },
          }}
        >
          <IconButton size="small" tabIndex={-1}>
            <FilterListRoundedIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle2">Фильтры</Typography>
          {hasFilters && <Chip size="small" label={activeFilters.length} />}
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" tabIndex={-1}>
            {filtersOpen ? (
              <ExpandLessRoundedIcon fontSize="small" />
            ) : (
              <ExpandMoreRoundedIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
        <Button
          size="small"
          variant="outlined"
          disabled={!hasFilters}
          onClick={(event) => {
            event.stopPropagation();
            onFiltersChange?.({ ...EMPTY_FILTERS });
          }}
        >
          Сбросить
        </Button>
      </Stack>
      {!filtersOpen && hasFilters && (
        <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap">
          {activeFilters.map((item) => (
            <Chip
              key={item.key}
              size="small"
              variant="outlined"
              label={`${item.label}: ${item.value}`}
              onDelete={() => updateFilter(item.key, '')}
              sx={{
                maxWidth: 240,
                '.MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          ))}
        </Stack>
      )}
      <Collapse
        in={filtersOpen}
        timeout={{ enter: 180, exit: 140 }}
        easing={{
          enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
          exit: 'cubic-bezier(0.4, 0, 1, 1)',
        }}
        unmountOnExit
      >
        <Box
          sx={{
            mt: 1,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              xl: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 1.25,
            alignItems: 'start',
            opacity: filtersOpen ? 1 : 0,
            transform: filtersOpen ? 'translateY(0)' : 'translateY(-6px)',
            transition: 'opacity 180ms ease, transform 180ms cubic-bezier(0.22, 1, 0.36, 1)',
            willChange: 'opacity, transform',
          }}
        >
          <TextField
            label="Статус"
            value={filters.status || ''}
            onChange={(e) => updateFilter('status', e.target.value)}
            size="small"
            select
            fullWidth
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          {!houseMode && (
            <TextField
              label={roomNumberLabel}
              value={filters.room_number || ''}
              onChange={(e) => updateFilter('room_number', e.target.value)}
              size="small"
              fullWidth
            />
          )}
          {!houseMode && (
            <TextField
              label="Суффикс"
              value={filters.suffix || ''}
              onChange={(e) => updateFilter('suffix', e.target.value)}
              size="small"
              fullWidth
            />
          )}
          <TextField
            label="Тип счётчика"
            value={filters.system_type || ''}
            onChange={(e) => updateFilter('system_type', e.target.value)}
            size="small"
            select
            fullWidth
          >
            {SYSTEM_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Производитель"
            value={filters.manufacturer || ''}
            onChange={(e) => updateFilter('manufacturer', e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Серийный №"
            value={filters.serial_number || ''}
            onChange={(e) => updateFilter('serial_number', e.target.value)}
            size="small"
            fullWidth
          />
        </Box>
        {hasFilters && (
          <Stack
            direction="row"
            spacing={0.5}
            mt={1.25}
            flexWrap="wrap"
            useFlexGap
            sx={{
              opacity: filtersOpen ? 1 : 0,
              transform: filtersOpen ? 'translateY(0)' : 'translateY(-4px)',
              transition: 'opacity 200ms ease 30ms, transform 200ms cubic-bezier(0.22, 1, 0.36, 1) 30ms',
            }}
          >
            {activeFilters.map((item) => (
              <Chip
                key={item.key}
                size="small"
                variant="outlined"
                label={`${item.label}: ${item.value}`}
                onDelete={() => updateFilter(item.key, '')}
                sx={{
                  maxWidth: 240,
                  '.MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            ))}
          </Stack>
        )}
      </Collapse>
    </Paper>
  );
}

export default function DevicesTable({
  rows = [],
  total = 0,
  page = 1,
  pageSize = 10,
  onPageChange,
  onDeviceUpdate,
  onDeviceReplaced,
  filters = {},
  onFiltersChange,
  sortBy,
  sortDir = 'asc',
  onSortChange,
  loading = false,
  roomNumberLabel = '№ помещения',
  houseMode = false,
}) {
  const navigate = useNavigate();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const columnCount = houseMode ? 13 : 16;

  const [editingId, setEditingId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDevice, setConfirmDevice] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [replacementsByApartment, setReplacementsByApartment] = useState({});
  const [replacementsLoading, setReplacementsLoading] = useState({});
  const [canManageDevices, detachingIds, detachError] = useUnit([
    $canManageDevices,
    $detachingDeviceIds,
    $detachDeviceError,
  ]);
  const editingDevice = useMemo(
    () => rows.find((r) => String(r.id) === String(editingId)) || null,
    [rows, editingId]
  );
  const [replacingId, setReplacingId] = useState(null);
  const replacingDevice = useMemo(
    () => rows.find((r) => String(r.id) === String(replacingId)) || null,
    [rows, replacingId]
  );
  const openEditor = (row) => setEditingId(row.id);
  const closeEditor = () => setEditingId(null);
  const openDeleteConfirm = (row) => {
    setConfirmDevice(row);
    setConfirmOpen(true);
  };
  const closeDeleteConfirm = () => {
    setConfirmOpen(false);
    setConfirmDevice(null);
  };
  const renderSortableHeader = (label, field) => {
    const active = sortBy === field;
    return (
      <TableSortLabel
        active={active}
        direction={active ? sortDir : 'asc'}
        onClick={() => onSortChange?.(field)}
      >
        {label}
      </TableSortLabel>
    );
  };

  const toggleExpand = async (row) => {
    const rowId = String(row.id);
    const next = new Set(expandedIds);
    if (next.has(rowId)) {
      next.delete(rowId);
      setExpandedIds(next);
      return;
    }
    next.add(rowId);
    setExpandedIds(next);

    const apartmentId = row.apartment_id;
    if (!apartmentId || replacementsByApartment[String(apartmentId)]) {
      return;
    }
    try {
      setReplacementsLoading((prev) => ({ ...prev, [String(apartmentId)]: true }));
      const data = await deviceReplacementsApi.listByApartment(String(apartmentId));
      setReplacementsByApartment((prev) => ({
        ...prev,
        [String(apartmentId)]: data || [],
      }));
    } catch (err) {
      console.error('load replacements failed', err);
      setReplacementsByApartment((prev) => ({
        ...prev,
        [String(apartmentId)]: [],
      }));
    } finally {
      setReplacementsLoading((prev) => ({ ...prev, [String(apartmentId)]: false }));
    }
  };

  const renderReplacementHistory = (device) => {
    const apartmentId = device?.apartment_id;
    if (!apartmentId) {
      return <Typography variant="body2">Нет данных о привязке.</Typography>;
    }
    const isLoading = replacementsLoading[String(apartmentId)];
    if (isLoading) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2">Загрузка истории замен…</Typography>
        </Stack>
      );
    }
    const items = replacementsByApartment[String(apartmentId)] || [];
    const related = items.filter(
      (r) => String(r.old_device_id) === String(device.id) || String(r.new_device_id) === String(device.id)
    );
    if (!related.length) {
      return <Typography variant="body2">Замен не было.</Typography>;
    }
    return (
      <List dense sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1 }}>
        {related.map((rep, idx) => (
          <React.Fragment key={rep.id}>
            <ListItem>
              <ListItemText
                primary={`Дата замены: ${new Date(rep.changed_at).toLocaleString('ru-RU')}`}
                secondary={
                  [
                    rep.created_by_name ? `Выполнил: ${rep.created_by_name}` : null,
                    rep.owner_email ? `Почта: ${rep.owner_email}` : null,
                    rep.act_sent_to ? `Акт отправлен: ${rep.act_sent_to}` : null,
                    rep.reason ? `Причина: ${rep.reason}` : null,
                    rep.comment ? `Комментарий: ${rep.comment}` : null,
                    rep.old_last_value != null ? `Последнее: ${rep.old_last_value}` : null,
                    rep.new_start_value != null ? `Старт: ${rep.new_start_value}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || '—'
                }
              />
              <Chip size="small" label={rep.new_device_serial || rep.old_device_serial || '—'} />
            </ListItem>
            {idx < related.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <>
      {detachError ? (
        <Alert severity="error" onClose={() => detachDeviceErrorCleared()} sx={{ mb: 1 }}>
          {detachError}
        </Alert>
      ) : null}
      <DevicesFiltersPanel
        filters={filters}
        onFiltersChange={onFiltersChange}
        roomNumberLabel={roomNumberLabel}
        houseMode={houseMode}
      />

      <Box sx={{ position: 'relative' }}>
        {loading && (
          <LinearProgress
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }}
          />
        )}
        <TableContainer
          component={Paper}
          sx={{ borderRadius: 3, overflowX: 'auto', maxWidth: '100%', width: '100%' }}
        >
          <Table size="small" sx={{ minWidth: houseMode ? 1120 : 1400, width: 'max-content' }}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Действия</TableCell>
              <TableCell>{renderSortableHeader('Статус', 'status')}</TableCell>
              {!houseMode && <TableCell>ID дома</TableCell>}
              {!houseMode && <TableCell>{renderSortableHeader(roomNumberLabel, 'room_number')}</TableCell>}
              {!houseMode && <TableCell>{renderSortableHeader('Суффикс', 'suffix')}</TableCell>}
              <TableCell>{renderSortableHeader('Тип счётчика', 'system_type')}</TableCell>
              <TableCell>Заряд %</TableCell>
              <TableCell>{renderSortableHeader('Производитель', 'manufacturer')}</TableCell>
              <TableCell>Инциденты</TableCell>
              <TableCell>{renderSortableHeader('Заводской №', 'serial_number')}</TableCell>
              <TableCell>Показания</TableCell>
              <TableCell>Показания на дату</TableCell>
              <TableCell>Время (история)</TableCell>
              <TableCell>{renderSortableHeader('Время прихода', 'last_seen_at')}</TableCell>
              <TableCell>Комментарий</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((d) => {
              const isExpanded = expandedIds.has(String(d.id));
              return (
                <React.Fragment key={d.id}>
                  <TableRow hover>
                    <TableCell sx={{ width: 48 }}>
                      <IconButton
                        size="small"
                        onClick={() => toggleExpand(d)}
                        aria-label="Показать детали"
                      >
                        {isExpanded ? (
                          <KeyboardArrowUpRoundedIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownRoundedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <IconButton
                          size="small"
                          aria-label="Редактировать устройство"
                          disabled={loading}
                          onClick={() => openEditor(d)}
                        >
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                        {canManageDevices && (
                          <IconButton
                            size="small"
                            aria-label="Заменить устройство"
                            disabled={loading || d.status === 'REPLACED'}
                            onClick={() => setReplacingId(d.id)}
                          >
                            <SwapHorizRoundedIcon fontSize="small" />
                          </IconButton>
                        )}
                        {canManageDevices && (
                          <Tooltip
                            title={
                              d.heat_archived || d.status === 'REPLACED'
                                ? `Архивный прибор остаётся в истории ${houseMode ? 'дома' : 'квартиры'}`
                                : `Убрать из ${houseMode ? 'дома' : 'квартиры'} без удаления данных`
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                aria-label={`Убрать устройство из ${houseMode ? 'дома' : 'квартиры'}`}
                                disabled={
                                  loading
                                  || detachingIds.has(String(d.id))
                                  || d.heat_archived
                                  || d.status === 'REPLACED'
                                }
                                onClick={() => openDeleteConfirm(d)}
                              >
                                <LinkOffRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell><StatusCell status={d.status} /></TableCell>
                    {!houseMode && <TableCell><CellText value={d.house_id ?? '-'} maxWidth={80} /></TableCell>}
                    {!houseMode && <TableCell>
                      {d.apartment_id ? (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => navigate(`/apartments/${d.apartment_id}?deviceId=${d.id}`)}
                          sx={{
                            minWidth: 0,
                            px: 0.75,
                            py: 0.2,
                            fontSize: 13,
                            fontWeight: 800,
                            color: '#2368B5',
                            textTransform: 'none',
                          }}
                        >
                          {d.room_number ?? '-'}
                        </Button>
                      ) : (
                        <CellText value={d.room_number ?? '-'} maxWidth={90} />
                      )}
                    </TableCell>}
                    {!houseMode && <TableCell><CellText value={d.suffix ?? '-'} maxWidth={70} /></TableCell>}
                    <TableCell><CellText value={d.system_type ?? '-'} maxWidth={140} /></TableCell>
                    <TableCell>
                      {isElectricityType(d.system_type) ? (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Tooltip title="Питание от сети">
                            <PowerRoundedIcon sx={{ color: '#2e7d32' }} fontSize="small" />
                          </Tooltip>
                          {/* <Typography variant="body2">Сеть</Typography> */}
                        </Stack>
                      ) : (
                        <CellText value={d.battery ?? '-'} maxWidth={80} />
                      )}
                    </TableCell>
                    <TableCell><CellText value={d.manufacturer ?? '-'} maxWidth={140} /></TableCell>
                    <TableCell>
                      {Array.isArray(d.incidents) && d.incidents.length
                        ? d.incidents.map((i, ii) => (
                          <Chip key={ii} size="small" label={i} sx={{ mr: 0.5, mb: 0.5 }} />
                        ))
                        : '-'}
                    </TableCell>
                    <TableCell><CellText value={d.serial_number ?? '-'} maxWidth={160} /></TableCell>
                    <TableCell>
                      <DeviceReadingCell device={d} />
                    </TableCell>
                    <TableCell>
                      <DeviceHistoryReadingCell device={d} />
                    </TableCell>
                    <TableCell>
                      <CellText value={d.history_ts ? new Date(d.history_ts).toLocaleString() : '-'} maxWidth={170} />
                    </TableCell>
                    <TableCell>
                      <CellText value={getDeviceArrivalTime(d) || '-'} maxWidth={170} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={d.description || d.comment || ''}>
                        <span
                          style={{
                            maxWidth: 260,
                            minWidth: 0,
                            display: 'inline-block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {d.description || d.comment || '?'}
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columnCount}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ padding: 2, background: 'rgba(0,0,0,0.02)' }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            История замен
                          </Typography>
                          {renderReplacementHistory(d)}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {loading && !rows.length && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            )}
            {!loading && !rows.length && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" style={{ padding: 24 }}>
                  Нет данных
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Stack direction="row" justifyContent="center" sx={{ mt: 1.5 }}>
        <Pagination
          count={pages}
          page={page}
          disabled={loading}
          onChange={(_, p) => onPageChange?.(p)}
        />
      </Stack>

      <ConfirmDialog
        open={confirmOpen}
        title={`Убрать прибор из ${houseMode ? 'дома' : 'квартиры'}`}
        description={`Убрать прибор «${confirmDevice?.serial_number || confirmDevice?.id || ""}» из ${houseMode ? 'дома' : 'квартиры'}? Показания, документы и настройки сохранятся в разделе «Непривязанные».`}
        confirmText="Убрать"
        cancelText="Отмена"
        confirmColor="error"
        onCancel={closeDeleteConfirm}
        onConfirm={() => {
          if (confirmDevice?.id) {
            detachDeviceClicked(String(confirmDevice.id));
          }
          closeDeleteConfirm();
        }}
      />
      <EditDeviceDialog
        open={Boolean(editingDevice)}
        device={editingDevice}
        onClose={closeEditor}
        onSave={onDeviceUpdate}
      />
      <ReplaceDeviceDialog
        open={Boolean(replacingDevice)}
        device={replacingDevice}
        onClose={() => setReplacingId(null)}
        onReplaced={onDeviceReplaced}
      />
    </>
  );
}
